/**
 * 会话状态（zustand）。
 *
 * 安全取舍（答辩口径：
 * - accessToken **只放内存**：刷新页面即丢失，必须用 refreshToken 换新的，降低 XSS 窃取面。
 * - refreshToken 存 localStorage：这是"免登录"的代价，后端已做哈希存储与撤销；生产可换成 httpOnly Cookie。
 * - user 也缓存一份：用于首屏立即渲染，随后用 GET /me 复核（服务端为准）。
 */
import { create } from 'zustand';
import type { SchoolId } from '@crossmatch/shared';

export type UserRole = 'student' | 'organizer' | 'admin';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  school: SchoolId;
  role: UserRole;
  /** 公开邀请 ID（CM-XXXXXX）；可分享给别人，让别人按 ID 找到我 */
  publicId?: string | null;
  /** 同意《用户协议》《隐私政策》的时间戳（ISO）；null = 未记录 */
  consentAcceptedAt?: string | null;
  createdAt?: string;
}

export interface SessionPayload {
  user: SessionUser;
  accessToken: string;
  refreshToken: string;
}

const REFRESH_STORAGE_KEY = 'crossmatch.refresh-token';
const USER_STORAGE_KEY = 'crossmatch.user';

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // 隐私模式或存储被禁用：内存态仍可正常使用，不阻塞登录
  }
}

function parseStoredUser(raw: string | null): SessionUser | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    if (typeof record.id !== 'string' || typeof record.email !== 'string' || typeof record.name !== 'string') {
      return null;
    }
    const school = typeof record.school === 'string' ? record.school : 'other';
    const role: UserRole =
      record.role === 'organizer' || record.role === 'admin' || record.role === 'student' ? record.role : 'student';
    const user: SessionUser = {
      id: record.id,
      email: record.email,
      name: record.name,
      school: school as SchoolId,
      role,
    };
    if (typeof record.createdAt === 'string') user.createdAt = record.createdAt;
    if (typeof record.publicId === 'string') user.publicId = record.publicId;
    if (typeof record.consentAcceptedAt === 'string') user.consentAcceptedAt = record.consentAcceptedAt;
    return user;
  } catch {
    return null;
  }
}

interface SessionState {
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;
  setSession: (payload: SessionPayload) => void;
  setTokens: (accessToken: string, refreshToken?: string) => void;
  setUser: (user: SessionUser | null) => void;
  clear: () => void;
}

export const useSession = create<SessionState>((set) => ({
  accessToken: null,
  refreshToken: readStorage(REFRESH_STORAGE_KEY),
  user: parseStoredUser(readStorage(USER_STORAGE_KEY)),

  setSession: (payload) => {
    writeStorage(REFRESH_STORAGE_KEY, payload.refreshToken);
    writeStorage(USER_STORAGE_KEY, JSON.stringify(payload.user));
    set({ accessToken: payload.accessToken, refreshToken: payload.refreshToken, user: payload.user });
  },

  setTokens: (accessToken, refreshToken) => {
    if (typeof refreshToken === 'string' && refreshToken.length > 0) {
      writeStorage(REFRESH_STORAGE_KEY, refreshToken);
      set({ accessToken, refreshToken });
      return;
    }
    set({ accessToken });
  },

  setUser: (user) => {
    writeStorage(USER_STORAGE_KEY, user ? JSON.stringify(user) : null);
    set({ user });
  },

  clear: () => {
    writeStorage(REFRESH_STORAGE_KEY, null);
    writeStorage(USER_STORAGE_KEY, null);
    set({ accessToken: null, refreshToken: null, user: null });
  },
}));

export function getAccessToken(): string | null {
  return useSession.getState().accessToken;
}

export function getRefreshToken(): string | null {
  return useSession.getState().refreshToken;
}

/** 是否持有可用的登录凭据（accessToken 在内存、refreshToken 在 localStorage） */
export function hasCredentials(): boolean {
  const state = useSession.getState();
  return Boolean(state.accessToken) || Boolean(state.refreshToken);
}